import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Skeleton,
  TablePagination
} from "@mui/material";
import {
  Calendar,
  Eye,
  Camera,
  Upload
} from "lucide-react";
import StatusBadge from "./StatusBadge";
import InstituteLogoCell from "./InstituteLogoCell";

const BookingsTable = ({
  events = [],
  isLoading = false,
  onOpenManageModal,
  onOpenSocialPosterModal,
  onPreviewFile,
  userRole = "designer",
  timelineStatus = "upcoming",
  totalCount = 0,
  page = 0,
  rowsPerPage = 10,
  onPageChange = null,
  onRowsPerPageChange = null
}) => {
  const isOngoingOrPast =
    timelineStatus === "ongoing" ||
    timelineStatus === "past" ||
    timelineStatus === "completed";

  const isCoordinatorOrAdmin =
    userRole === "admin" ||
    userRole === "coordinator" ||
    (userRole && userRole !== "designer");

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        borderRadius: "12px",
        border: "1px solid #e2e8f0",
        backgroundColor: "#ffffff",
        overflowX: "auto"
      }}
    >
      <Table sx={{ minWidth: 700 }}>
        <TableHead sx={{ backgroundColor: "#f8fafc" }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b", py: 1.5, width: "45px" }}>
              #
            </TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b", py: 1.5, minWidth: "150px" }}>
              Institute Name
            </TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b", py: 1.5, minWidth: "60px" }}>
              Edition No
            </TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b", py: 1.5, minWidth: "120px" }}>
              Domain Name
            </TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b", py: 1.5 }}>
              Event Type
            </TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b", py: 1.5, minWidth: "135px" }}>
              Event Duration
            </TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b", py: 1.5 }}>
              {isOngoingOrPast
                ? "Social Poster Status"
                : isCoordinatorOrAdmin
                ? "Banner Status"
                : "Approval Status"}
            </TableCell>
            <TableCell
              align="center"
              sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b", py: 1.5, minWidth: "90px" }}
            >
              Institute Logo
            </TableCell>
            <TableCell
              align="center"
              sx={{
                fontWeight: 700,
                fontSize: "0.75rem",
                color: "#64748b",
                py: 1.5,
                minWidth: isOngoingOrPast ? "200px" : isCoordinatorOrAdmin ? "100px" : "130px"
              }}
            >
              {isOngoingOrPast || isCoordinatorOrAdmin ? "Action" : "Upload Banner"}
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {isLoading ? (
            Array.from({ length: 5 }).map((_, idx) => (
              <TableRow key={`skeleton-${idx}`}>
                <TableCell><Skeleton width={20} height={20} /></TableCell>
                <TableCell><Skeleton width={140} height={24} /></TableCell>
                <TableCell><Skeleton width={50} height={24} /></TableCell>
                <TableCell><Skeleton width={110} height={24} /></TableCell>
                <TableCell><Skeleton width={60} height={24} /></TableCell>
                <TableCell><Skeleton width={120} height={20} /></TableCell>
                <TableCell><Skeleton width={85} height={24} /></TableCell>
                <TableCell align="center"><Skeleton width={32} height={32} sx={{ mx: "auto", borderRadius: "6px" }} /></TableCell>
                <TableCell align="center">
                  <Skeleton
                    width={isOngoingOrPast ? 180 : isCoordinatorOrAdmin ? 75 : 32}
                    height={32}
                    sx={{
                      mx: "auto",
                      borderRadius: isOngoingOrPast || isCoordinatorOrAdmin ? "4px" : "6px"
                    }}
                  />
                </TableCell>
              </TableRow>
            ))
          ) : events.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 6, color: "#94a3b8" }}>
                <Typography variant="body2" sx={{ fontStyle: "italic", fontSize: "0.85rem" }}>
                  No prerequisite requests found.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            events.map((event, index) => {
              const eventType = (event.event_type || event.type || "FDP").toUpperCase();
              return (
                <TableRow
                  key={event.request_id || event.bookslot_id || event.id || index}
                  hover
                  sx={{
                    "&:hover": { backgroundColor: "#f8fafc" },
                    transition: "background-color 0.15s ease"
                  }}
                >
                  {/* S.No */}
                  <TableCell sx={{ fontSize: "0.78rem", color: "#94a3b8", fontWeight: 600 }}>
                    {page * rowsPerPage + index + 1}
                  </TableCell>

                  {/* Institute Name */}
                  <TableCell sx={{ minWidth: 150 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: "#0f172a",
                        fontSize: "0.85rem",
                        lineHeight: 1.3
                      }}
                    >
                      {event.institute_name || event.instituteName || "Unknown Institute"}
                    </Typography>
                  </TableCell>

                  {/* Edition No */}
                  <TableCell sx={{ minWidth: 60 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: "#0f172a",
                        fontSize: "0.82rem"
                      }}
                    >
                      {event.edition_no
                        ? (String(event.edition_no).startsWith("#") ? String(event.edition_no) : `#${event.edition_no}`)
                        : event.edition
                        ? (String(event.edition).startsWith("#") ? String(event.edition) : `#${event.edition}`)
                        : event.edition_number
                        ? (String(event.edition_number).startsWith("#") ? String(event.edition_number) : `#${event.edition_number}`)
                        : "—"}
                    </Typography>
                  </TableCell>

                  {/* Domain Name */}
                  <TableCell sx={{ minWidth: 120 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: "#334155",
                        fontSize: "0.82rem",
                        lineHeight: 1.3
                      }}
                    >
                      {event.domain_name || event.domainName || "—"}
                    </Typography>
                  </TableCell>

                  {/* Event Type */}
                  <TableCell>
                    <Chip
                      label={eventType}
                      size="small"
                      sx={{
                        height: "20px",
                        fontSize: "0.6875rem",
                        fontWeight: 700,
                        backgroundColor:
                          eventType === "FDP" ? "#eff6ff" : "#f0fdf4",
                        color: eventType === "FDP" ? "#1877f2" : "#16a34a",
                        border: `1px solid ${
                          eventType === "FDP" ? "#bfdbfe" : "#bbf7d0"
                        }`,
                        borderRadius: "4px"
                      }}
                    />
                  </TableCell>

                  {/* Event Duration */}
                  <TableCell sx={{ minWidth: 135 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        color: "#0f172a",
                        fontSize: "0.78rem",
                        fontWeight: 500,
                        whiteSpace: "nowrap"
                      }}
                    >
                      <Calendar size={13} color="#1877f2" />
                      <span>
                        {(event.start_date || event.startDate) && (event.end_date || event.endDate)
                          ? `${event.start_date || event.startDate} – ${event.end_date || event.endDate}`
                          : (event.start_date || event.startDate || event.end_date || event.endDate || "—")}
                      </span>
                    </Box>
                  </TableCell>

                  {/* Review / Approval Status */}
                  <TableCell>
                    <StatusBadge
                      status={
                        isOngoingOrPast
                          ? (event.poster_upload_status || event.status)
                          : (event.status || event.approval_status)
                      }
                    />
                  </TableCell>

                  {/* Institute Logo Column (Left of Action) */}
                  <TableCell align="center">
                    <InstituteLogoCell
                      logoKey={event.institute_logo || event.hosted_institute_logo || event.logo}
                      instituteName={event.institute_name || event.instituteName}
                    />
                  </TableCell>

                  {/* Action Column */}
                  <TableCell align="center">
                    {event.timelineStatus === "ongoing" ||
                    event.timelineStatus === "past" ||
                    event.timelineStatus === "completed" ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1
                        }}
                      >
                        <Tooltip title="View & Download Geo-Tagged Venue Photos" arrow placement="top">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Camera size={13} />}
                            onClick={() => onOpenManageModal(event)}
                            sx={{
                              py: 0.45,
                              px: 1.25,
                              fontSize: "0.72rem",
                              fontWeight: 700,
                              textTransform: "none",
                              borderRadius: "6px",
                              backgroundColor: "#eff6ff",
                              color: "#1877f2",
                              borderColor: "#bfdbfe",
                              whiteSpace: "nowrap",
                              "&:hover": {
                                backgroundColor: "#dbeafe",
                                borderColor: "#1877f2"
                              }
                            }}
                          >
                            Geo-Tag Photos
                          </Button>
                        </Tooltip>

                        <Tooltip title="Upload & Submit Social Media Poster for Admin Approval" arrow placement="top">
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<Upload size={13} />}
                            onClick={() => onOpenSocialPosterModal(event)}
                            sx={{
                              py: 0.45,
                              px: 1.25,
                              fontSize: "0.72rem",
                              fontWeight: 700,
                              textTransform: "none",
                              borderRadius: "6px",
                              whiteSpace: "nowrap",
                              backgroundColor: "#1877f2",
                              color: "#ffffff",
                              boxShadow: "none",
                              "&:hover": {
                                backgroundColor: "#1465cc",
                                boxShadow: "none"
                              }
                            }}
                          >
                            Social Poster
                          </Button>
                        </Tooltip>
                      </Box>
                    ) : isCoordinatorOrAdmin ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Eye size={13} />}
                          onClick={() => onOpenManageModal(event)}
                          sx={{
                            py: 0.4,
                            px: 1.5,
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            textTransform: "none",
                            borderRadius: "6px",
                            backgroundColor: "transparent",
                            color: "#1877f2",
                            borderColor: "#bfdbfe",
                            whiteSpace: "nowrap",
                            "&:hover": {
                              backgroundColor: "#eff6ff",
                              borderColor: "#1877f2"
                            }
                          }}
                        >
                          Review
                        </Button>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                      >
                        <Tooltip title="Upload Banner" arrow placement="top">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => onOpenManageModal(event)}
                              sx={{
                                p: 0.5,
                                borderRadius: "6px",
                                border: "1px solid #bfdbfe",
                                backgroundColor: "#eff6ff",
                                color: "#1877f2",
                                transition: "all 0.15s ease",
                                "&:hover": {
                                  backgroundColor: "#dbeafe",
                                  borderColor: "#1877f2"
                                }
                              }}
                            >
                              <Upload size={14} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {totalCount > 0 && onPageChange && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ borderTop: "1px solid #e2e8f0" }}
        />
      )}
    </TableContainer>
  );
};

export default BookingsTable;
